// Кто сколько говорил: доля каждого участника в размеченной расшифровке.
//
// Считается по тексту записи, потому что другого источника нет: экран
// распознавания сохраняет в Историю строки «Имя: реплика» БЕЗ таймкодов
// (`transcriptText` в RecognitionScreen.js), а отрезки с временем в записи не
// хранятся. Поэтому два способа:
//  - почти у всех реплик (≥90%) есть отрезок «[00:00:01 - 00:00:05]» — считаем
//    время, реплики без отрезка не считаем: одна вставленная руками строка
//    «Время: 10:30» не должна обнулять расчёт по времени для всей встречи;
//  - иначе считаем слова. Смешивать нельзя: секунды одних и слова других в
//    одну сумму не складываются.
import { parseTranscriptText } from './transcriptParse';

const RANGE_RE = /^\[(\d{1,2}):(\d{2}):(\d{2}(?:\.\d+)?)\s*-\s*(\d{1,2}):(\d{2}):(\d{2}(?:\.\d+)?)\]$/;

function rangeSeconds(timecode) {
  const m = (timecode || '').match(RANGE_RE);
  if (!m) return null;
  const at = (h, mm, s) => Number(h) * 3600 + Number(mm) * 60 + Number(s);
  return Math.max(0, at(m[4], m[5], m[6]) - at(m[1], m[2], m[3]));
}

const wordCount = (text) => (text || '').split(/\s+/).filter(Boolean).length;

/**
 * Текст → { basis: 'time' | 'words', shares: [{ speaker, percent, label }] }, по
 * убыванию доли; проценты целые и в сумме дают 100. Меньше двух участников —
 * null: «один человек сказал 100%» ничего не сообщает.
 */
export function speakerShares(text) {
  const fragments = (parseTranscriptText(text) || []).filter((f) => f.speaker);
  const seconds = fragments.map((f) => rangeSeconds(f.timecode));
  const timed = seconds.filter((s) => s !== null).length;

  const tally = (basis) => {
    const totals = new Map();
    fragments.forEach((f, i) => {
      if (basis === 'time' && seconds[i] === null) return;
      const weight = basis === 'time' ? seconds[i] : wordCount(f.text);
      totals.set(f.speaker, (totals.get(f.speaker) || 0) + weight);
    });
    return { basis, totals, sum: [...totals.values()].reduce((a, b) => a + b, 0) };
  };
  // По словам и тогда, когда по времени считать нечего: отрезки нулевой длины
  // (пустая сумма) или второй участник говорил только без отрезков.
  let counted = fragments.length && timed >= fragments.length * 0.9 ? tally('time') : null;
  if (!counted || !counted.sum || counted.totals.size < 2) counted = tally('words');
  const { basis, totals, sum } = counted;
  if (totals.size < 2 || !sum) return null;

  // Наибольший остаток: простое округление даёт 99% или 101% в сумме.
  const raw = [...totals].map(([speaker, w]) => ({ speaker, exact: (w * 100) / sum }));
  raw.forEach((r) => { r.percent = Math.floor(r.exact); });
  let left = 100 - raw.reduce((a, r) => a + r.percent, 0);
  [...raw].sort((a, b) => (b.exact - b.percent) - (a.exact - a.percent))
    .forEach((r) => { if (left > 0) { r.percent += 1; left -= 1; } });

  return {
    basis,
    // Доля есть, но меньше процента — «<1%», а не «0%»: человек же говорил.
    shares: raw.sort((a, b) => b.exact - a.exact).map(({ speaker, percent, exact }) => ({
      speaker, percent, label: percent === 0 && exact > 0 ? '<1%' : `${percent}%`,
    })),
  };
}
